import React, { useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCheck } from "go-form";
import { LimitComponentVisibility } from "go-core/components/LimitComponentVisibility";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import {
	PaymentMethodApi,
	PaymentMethodCustomFieldTemplateApi,
	VenueRoleApi,
} from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";

interface Props {
	form: UseFormReturn<PaymentMethodApi>;
	paymentMethod?: PaymentMethodApi;
}

const PaymentMethodRolesForm = ({ form, paymentMethod }: Props) => {
	const { t } = useTranslation();
	const {
		formState: { errors },
		control,
		watch,
		setValue,
		register,
	} = form;
	const limitAmount = watch("limit.amount");
	const minAmount = watch("min_amount.amount");
	const customFieldTemplates = watch("custom_field_templates");
	const printCopy = watch("print_copy");
	const priceWasted = watch("price_wasted");
	const expandLimitedContent = useRef<boolean>(
		Boolean(
			limitAmount ||
				minAmount ||
				(customFieldTemplates && customFieldTemplates.length !== 0) ||
				printCopy ||
				priceWasted
		)
	);

	const searchRoles = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getRolesSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const searchCustomFields = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		const newParams = {
			...params,
			"resource_type|e": "PAYMENT_METHOD",
		};
		return api.organization().getCustomFieldsSearchSelect(search, newParams, {
			cancelToken: options?.token,
		});
	};

	const getRoleTags = (roles?: VenueRoleApi[]) => {
		return roles?.map((role) => {
			return {
				label: role.role_name,
				id: role.id,
			};
		});
	};

	const getCustomFieldTemplates = (customFieldTemplates?: PaymentMethodCustomFieldTemplateApi[]) => {
		return customFieldTemplates?.map((customFieldTemplate) => {
			return {
				label: customFieldTemplate.name,
				name: customFieldTemplate.name,
				slug: customFieldTemplate.slug,
			};
		});
	};

	const handleChangeCustomFieldTemplates = (customFieldTemplateObjects: CustomFieldTemplateApi[]) => {
		const parsedCustomFieldTemplates = customFieldTemplateObjects.map((customFieldTemplateObject) => ({
			slug: customFieldTemplateObject.slug,
			name: customFieldTemplateObject.name,
		}));
		setValue("custom_field_templates", parsedCustomFieldTemplates);
	};

	return (
		<fieldset className="form-group no-gutters">
			<h5>
				{t("modules.payment_method.field.roles.title")}
				<small className="text-muted">
					<br />
					{t("modules.payment_method.field.roles.help_text.description")}
				</small>
			</h5>
			<FormSelectGroup
				label={t("modules.payment_method.field.roles.label.title")}
				isMulti
				name="roles"
				errors={errors}
				defaultValue={getRoleTags(paymentMethod?.roles || [])}
				getOptionLabel={(option) => option.label}
				getOptionValue={(option) => option.id}
				loadOptions={searchRoles}
				control={control}
				data-testid="roles"
			/>
			<LimitComponentVisibility
				id="advanced"
				defaultVisible={expandLimitedContent.current}
				legendText={t("common.word.advanced", { ns: "lib" })}
				unlimitedContent={
					<>
						<FormNumberInput
							label={t("modules.payment_method.field.payment_limit.title")}
							suffix={""}
							control={control}
							name="limit.amount"
							errors={errors}
							help={t("modules.payment_method.field.payment_limit.help_text.title")}
						/>
						<FormNumberInput
							label={t("modules.payment_method.field.payment_min_amount.title")}
							suffix={""}
							control={control}
							name="min_amount.amount"
							errors={errors}
							help={t("modules.payment_method.field.payment_min_amount.help_text.title")}
						/>
						<FormSelectGroup
							label={t("modules.payment_method.field.custom_field_templates.title")}
							name="custom_field_templates"
							errors={errors}
							loadOptions={searchCustomFields}
							control={control}
							isMulti
							onChange={(_, fullObj) => handleChangeCustomFieldTemplates(fullObj)}
							defaultValue={getCustomFieldTemplates(paymentMethod?.custom_field_templates || [])}
							getOptionLabel={(option) => option.label}
							getOptionValue={(option) => option.slug}
							data-testid="custom_field_templates"
							help={t("modules.payment_method.field.custom_field_templates.help_text.title")}
						/>
						<FormCheck
							type="switch"
							register={register}
							label={t("modules.payment_method.field.print_copy.title")}
							name="print_copy"
							errors={errors}
							help={t("modules.payment_method.field.print_copy.help_text.title")}
						/>
						<FormCheck
							type="switch"
							register={register}
							label={t("modules.payment_method.field.price_wasted.title")}
							name="price_wasted"
							errors={errors}
							help={t("modules.payment_method.field.price_wasted.help_text.title")}
						/>
					</>
				}
			/>
		</fieldset>
	);
};

export default PaymentMethodRolesForm;
